import {
  Bot,
  BrainCircuit,
  Activity,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ChevronsLeft,
  ChevronsRight,
  FileBarChart,
  FolderTree,
  GitBranch,
  Globe,
  Hash,
  LayoutDashboard,
  Lightbulb,
  ListChecks,
  LogOut,
  MessageSquare, // eslint-disable-line no-unused-vars -- kept for future re-add
  Network,
  Quote,
  ScrollText,
  FlaskConical,
  Send,
  Settings,
  Shield,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { useSystemActivity } from "@/hooks/useSystemActivity";
import { prefetchByPath } from "@/lib/queryClient";
import { cn } from "@/lib/utils";

const SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", end: true }],
  },
  {
    label: "Knowledge",
    items: [
      { to: "/universe", icon: Globe, label: "Universe" },
      { to: "/editorial", icon: Lightbulb, label: "Editorial" },
      { to: "/knowledge-base", icon: FolderTree, label: "Knowledge Base" },
    ],
  },
  {
    label: "Information Product",
    items: [
      { to: "/daily-briefs", icon: Send, label: "Daily Briefs" },
      { to: "/weekly-briefs", icon: CalendarDays, label: "Weekly Briefs" },
      { to: "/monthly-briefs", icon: CalendarClock, label: "Monthly Briefs" },
      { to: "/weekly-quotes", icon: Quote, label: "Weekly Quotes" },
      { to: "/weekly-surveys", icon: ListChecks, label: "Weekly Survey" },
      { to: "/weekly-numbers", icon: Hash, label: "By The Numbers" },
    ],
  },
  {
    label: "Intelligence Product",
    items: [
      { to: "/monthly-trends", icon: TrendingUp, label: "Monthly Trends" },
      { to: "/per-event-recap", icon: CalendarCheck, label: "Per Event Post-Event Recap" },
      { to: "/quarterly-state-industry", icon: FileBarChart, label: "Quarterly State of the Industry Report" },
      { to: "/monthly-pulse", icon: Activity, label: "Monthly The Pulse" },
    ],
  },
  {
    label: "Orchestration",
    items: [
      { to: "/knowledge", icon: Network, label: "Knowledge" },
      { to: "/memory", icon: BrainCircuit, label: "Memory" },
      { to: "/agents", icon: Bot, label: "Agents" },
      { to: "/workflows", icon: GitBranch, label: "Workflows" },
      { to: "/logs", icon: ScrollText, label: "Logs" },
      { to: "/experiments", icon: FlaskConical, label: "Experiments" },
    ],
  },
  {
    label: "System",
    items: [{ to: "/settings", icon: Settings, label: "Settings" }],
  },
];

export default function Sidebar({ user, onCommandPalette, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  const { activeRuns } = useSystemActivity();

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "relative z-20 flex flex-col shrink-0 border-r border-hm-border bg-hm-bg transition-[width] duration-200",
        collapsed ? "w-[64px]" : "w-[240px]",
      )}
    >
      {/* Turn AX · Beyond Activ wordmark. Native dark-navy background in
          the artwork blends into the sidebar; no container needed.
          Source: /public/logos/beyond-activ.png (1264×848, ~485 KB). */}
      {!collapsed && (
        <Link
          to="/dashboard"
          data-testid="sidebar-brand-logo"
          className="block px-4 pt-5 pb-2"
          aria-label="Beyond Activ — home"
        >
          <img
            src="/logos/beyond-activ.png"
            alt="Beyond Activ"
            style={{ maxWidth: 120, width: "100%", height: "auto",
                     display: "block", marginInline: "auto" }}
            draggable={false}
          />
        </Link>
      )}

      {/* Logo (Hail Mary Intelligence OS) — untouched */}
      <div className={cn("h-14 flex items-center gap-2 px-4 border-b border-hm-border", collapsed && "justify-center px-0")}>
        <span
          className={cn(
            "hm-dot",
            activeRuns > 0 ? "hm-dot--fast" : "hm-dot--pulse",
          )}
          data-testid="sidebar-pulse"
          title={activeRuns > 0 ? `${activeRuns} agent(s) active` : "System idle"}
        />
        {!collapsed && (
          <div className="flex flex-col leading-none">
            <span className="text-hm-fg font-semibold tracking-tight">Hail Mary</span>
            <span className="mono text-[9px] uppercase tracking-[0.24em] text-hm-fg-subtle mt-1">
              Intelligence OS
            </span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 min-h-0 overflow-y-auto py-4">
        {SECTIONS.map((section) => (
          <div key={section.label} className="mb-5">
            {!collapsed && (
              <div className="px-4 mb-2 mono text-[10px] uppercase tracking-[0.22em] text-hm-fg-subtle">
                {section.label}
              </div>
            )}
            <ul className="space-y-0.5">
              {section.items.map(({ to, icon: Icon, label, end }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    end={end}
                    data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    onMouseEnter={() => prefetchByPath(to)}
                    onFocus={() => prefetchByPath(to)}
                    className={({ isActive }) =>
                      cn(
                        "group relative flex items-center gap-3 mx-2 px-3 py-2 rounded-md text-sm transition-colors",
                        isActive
                          ? "text-hm-fg bg-hm-bg-1"
                          : "text-hm-fg-muted hover:text-hm-fg hover:bg-hm-bg-1",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-hm-accent" />
                        )}
                        <Icon className="h-4 w-4 shrink-0" />
                        {!collapsed && <span className="truncate">{label}</span>}
                      </>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className={cn("border-t border-hm-border p-3 space-y-2", collapsed && "px-2")}>
        {user && !collapsed && (
          <div className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-hm-bg-1">
            <div className="h-7 w-7 rounded-full bg-hm-bg-2 border border-hm-border-strong flex items-center justify-center text-[11px] text-hm-fg-muted">
              {user.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-hm-fg truncate">{user.name || user.email}</div>
              <div className="mono text-[10px] text-hm-fg-subtle truncate">{user.email}</div>
            </div>
          </div>
        )}
        {!collapsed ? (
          <button
            data-testid="cmdk-hint-btn"
            onClick={onCommandPalette}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-hm-fg-muted hover:text-hm-fg hover:bg-hm-bg-1 rounded-md transition-colors"
          >
            <Sparkles className="h-3.5 w-3.5 text-hm-accent" />
            <span>Command…</span>
            <kbd className="ml-auto mono text-[10px] px-1.5 py-0.5 rounded border border-hm-border bg-hm-bg-2 text-hm-fg-muted">
              ⌘K
            </kbd>
          </button>
        ) : (
          <button
            onClick={onCommandPalette}
            className="w-full flex items-center justify-center py-2 rounded-md hover:bg-hm-bg-1"
          >
            <Sparkles className="h-4 w-4 text-hm-accent" />
          </button>
        )}
        {!collapsed && (
          <Link
            to="/privacy"
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-hm-fg-muted hover:text-hm-fg hover:bg-hm-bg-1 rounded-md transition-colors"
            data-testid="sidebar-privacy"
          >
            <Shield className="h-3.5 w-3.5" />
            Privacy Policy
          </Link>
        )}
        {!collapsed && onLogout && (
          <button
            data-testid="sidebar-logout"
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-hm-fg-muted hover:text-hm-danger hover:bg-hm-bg-1 rounded-md transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        )}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-center py-1.5 rounded-md text-hm-fg-subtle hover:text-hm-fg hover:bg-hm-bg-1"
          data-testid="sidebar-collapse"
          title={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? (
            <ChevronsRight className="h-3.5 w-3.5" />
          ) : (
            <ChevronsLeft className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    </aside>
  );
}
